<template>
  <v-card outlined class="mt-5">
    <v-card-title class="d-flex align-center">
      <h2>Facility Equipment Checks</h2>
      <v-spacer></v-spacer>
      <v-chip small outlined class="mr-2" v-if="scheduledDayNames">
        <v-icon left small>mdi-calendar-week</v-icon>
        {{ scheduledDayNames }}
      </v-chip>
      <v-btn
        v-if="can('facilities.manage_check_schedule')"
        small
        outlined
        color="primary"
        @click="openSchedule"
      >
        <v-icon left small>mdi-calendar-edit</v-icon> Schedule
      </v-btn>
    </v-card-title>

    <!-- Schedule dialog (admin) -->
    <v-dialog v-model="scheduleDialog" max-width="420">
      <v-card>
        <v-card-title>Equipment Check Schedule</v-card-title>
        <v-card-text>
          <p class="body-2 grey--text text--darken-1">
            Pick the weekdays the equipment check may be submitted on.
          </p>
          <v-select
            v-model="scheduleDays"
            :items="weekdayOptions"
            label="Scheduled days"
            multiple
            chips
            small-chips
            outlined
            dense
            :rules="[(v) => (v && v.length >= 1) || 'Pick at least one day']"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="savingSchedule"
            @click="scheduleDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingSchedule"
            :disabled="!scheduleDays.length"
            @click="saveSchedule"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-tabs v-model="activeTab">
      <v-tab>Submit Check</v-tab>
      <v-tab>History</v-tab>
    </v-tabs>
    <v-tabs-items v-model="activeTab">
      <!-- Submit Check -->
      <v-tab-item>
        <v-card-text v-if="loadingToday" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-text>

        <!-- Already submitted today (read-only) -->
        <v-card-text v-else-if="todayCheck && !editing">
          <v-alert type="success" outlined>
            <div class="d-flex align-center">
              <span>Today's equipment check has already been submitted.</span>
              <v-spacer></v-spacer>
              <v-btn
                v-if="can('facilities.edit_equipment_check')"
                small
                color="primary"
                outlined
                @click="startEdit"
              >
                <v-icon left small>mdi-pencil</v-icon> Edit
              </v-btn>
            </div>
          </v-alert>
          <p class="caption grey--text mb-0">Date</p>
          <p>{{ todayCheck.check_date }}</p>
          <div v-for="section in todaySections" :key="section.title">
            <p class="subtitle-2 mt-4 mb-2">{{ section.title }}</p>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Equipment</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in section.rows" :key="item.id">
                    <td>
                      {{ item.equipment ? item.equipment.name : "—" }}
                    </td>
                    <td>
                      {{
                        item.equipment
                          ? item.equipment.designated_location
                          : "—"
                      }}
                    </td>
                    <td>
                      <v-chip small :color="statusColor(item.status)" dark>
                        {{ statusLabel(item.status) }}
                      </v-chip>
                    </td>
                    <td>{{ item.remarks || "—" }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <template v-if="todayCheck.notes">
            <p class="caption grey--text mb-0 mt-4">Notes</p>
            <p>{{ todayCheck.notes }}</p>
          </template>
        </v-card-text>

        <!-- Edit already-submitted check -->
        <v-card-text v-else-if="todayCheck && editing">
          <v-alert type="info" outlined dense>
            Editing today's submitted equipment check.
          </v-alert>
          <p class="subtitle-1">Equipment Checklist</p>
          <template v-if="editItems.length">
            <div
              v-for="section in editSections"
              :key="section.title"
              class="mb-4"
            >
              <p class="subtitle-2 mb-2">{{ section.title }}</p>
              <v-simple-table class="checklist-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width: 24%">Equipment</th>
                      <th
                        v-for="col in statusColumns"
                        :key="col.value"
                        class="text-center"
                      >
                        {{ col.label }}
                      </th>
                      <th style="width: 22%">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in section.rows"
                      :key="item.facility_equipment_id"
                    >
                      <td>
                        <strong>{{ item.name }}</strong>
                        <div class="caption grey--text">
                          {{ item.designated_location }}
                        </div>
                      </td>
                      <td
                        v-for="col in statusColumns"
                        :key="col.value"
                        class="text-center"
                      >
                        <v-simple-checkbox
                          :value="item.status === col.value"
                          :color="col.color"
                          :ripple="false"
                          @input="item.status = col.value"
                        ></v-simple-checkbox>
                      </td>
                      <td>
                        <v-text-field
                          v-model="item.remarks"
                          dense
                          hide-details
                          placeholder="Remarks..."
                        ></v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </template>

          <v-divider class="my-4"></v-divider>
          <v-textarea
            v-model="editForm.notes"
            label="Notes"
            rows="2"
            auto-grow
            outlined
          ></v-textarea>
          <v-btn color="primary" :loading="saving" @click="saveEdit">
            <v-icon left>mdi-content-save</v-icon> Save Changes
          </v-btn>
          <v-btn text class="ml-2" :disabled="saving" @click="cancelEdit">
            Cancel
          </v-btn>
        </v-card-text>

        <!-- Checklist form -->
        <v-card-text v-else>
          <v-alert v-if="!isScheduledToday" type="warning" outlined dense>
            Equipment checks are scheduled on
            <strong>{{ scheduledDayNames }}</strong
            >. Today is not a scheduled day, so submission is disabled.
          </v-alert>
          <p class="subtitle-1">Equipment Checklist</p>
          <template v-if="items.length">
            <div
              v-for="section in formSections"
              :key="section.title"
              class="mb-4"
            >
              <p class="subtitle-2 mb-2">{{ section.title }}</p>
              <v-simple-table class="checklist-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width: 24%">Equipment</th>
                      <th
                        v-for="col in statusColumns"
                        :key="col.value"
                        class="text-center"
                      >
                        {{ col.label }}
                      </th>
                      <th style="width: 22%">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in section.rows"
                      :key="item.facility_equipment_id"
                    >
                      <td>
                        <strong>{{ item.name }}</strong>
                        <div class="caption grey--text">
                          {{ item.designated_location }}
                        </div>
                      </td>
                      <td
                        v-for="col in statusColumns"
                        :key="col.value"
                        class="text-center"
                      >
                        <v-simple-checkbox
                          :value="item.status === col.value"
                          :color="col.color"
                          :ripple="false"
                          @input="item.status = col.value"
                        ></v-simple-checkbox>
                      </td>
                      <td>
                        <v-text-field
                          v-model="item.remarks"
                          dense
                          hide-details
                          placeholder="Remarks..."
                        ></v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </template>
          <v-alert v-else-if="loadError" type="error" outlined>
            {{ loadError }}
          </v-alert>
          <v-alert v-else type="info" outlined>
            No active equipment found. Add equipment in the Facility Equipment
            widget first.
          </v-alert>

          <v-divider class="my-4"></v-divider>
          <v-textarea
            v-model="form.notes"
            label="Notes"
            rows="2"
            auto-grow
            outlined
          ></v-textarea>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!items.length || !isScheduledToday"
            @click="submit"
          >
            <v-icon left>mdi-send</v-icon> Submit Check
          </v-btn>
        </v-card-text>
      </v-tab-item>

      <!-- History -->
      <v-tab-item>
        <v-card class="my-4" flat outlined>
          <v-card-text class="pb-2">
            <v-row align="center" dense>
              <v-col cols="12" sm="6" md="2">
                <v-menu
                  v-model="fromMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.from_date"
                      label="From Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.from_date"
                    no-title
                    @input="fromMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-menu
                  v-model="toMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.to_date"
                      label="To Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.to_date"
                    no-title
                    @input="toMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="2"
                v-if="can('reports.branch_filter')"
              >
                <v-select
                  v-model="filters.branch_id"
                  :items="branches"
                  item-text="branch_name"
                  item-value="id"
                  label="Branch"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  @click="search"
                  :loading="loadingHistory"
                >
                  <v-icon left>mdi-magnify</v-icon>Search
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-data-table :items="equipment_checks" :headers="historyHeaders">
          <template v-slot:item.branch="{ item }">
            {{ item.branch ? item.branch.branch_name : "—" }}
          </template>
          <template v-slot:item.checked_by="{ item }">
            {{
              item.checked_by
                ? item.checked_by.full_name || item.checked_by.username
                : "—"
            }}
          </template>
          <template v-slot:item.flagged_count="{ item }">
            <v-chip
              small
              :color="item.flagged_count > 0 ? 'red' : 'green'"
              dark
            >
              {{ item.flagged_count }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" @click="viewCheck(item)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        <equipment-check-detail-modal
          :show.sync="detailDialog"
          :check-id="selectedCheckId"
        />
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import EquipmentCheckDetailModal from "./equipmentCheckDetailModal.vue";
import { mapActions, mapGetters } from "vuex";
export default {
  components: { EquipmentCheckDetailModal },
  computed: {
    ...mapGetters({
      equipment_check_today: "facilities/equipment_check_today",
      equipment_checks: "facilities/equipment_checks",
      check_settings: "facilities/check_settings",
      branches: "branch/branches",
      hasPermission: "permission/hasPermission",
    }),
    todayCheck() {
      return this.equipment_check_today
        ? this.equipment_check_today.check
        : null;
    },
    scheduledDays() {
      return (
        (this.check_settings && this.check_settings.equipment_check_days) || []
      );
    },
    scheduledDayNames() {
      const names = this.weekdayOptions;
      return this.scheduledDays
        .map((d) => {
          const opt = names.find((o) => o.value === d);
          return opt ? opt.text : d;
        })
        .join(" & ");
    },
    isScheduledToday() {
      if (!this.scheduledDays.length) return true;
      return this.scheduledDays.includes(new Date().getDay());
    },
    formSections() {
      return this.groupByType(this.items);
    },
    editSections() {
      return this.groupByType(this.editItems);
    },
    todaySections() {
      return this.groupByType(this.todayCheck ? this.todayCheck.items : []);
    },
  },
  data() {
    return {
      activeTab: 0,
      loadingToday: false,
      submitting: false,
      saving: false,
      editing: false,
      loadingHistory: false,
      fromMenu: false,
      toMenu: false,
      detailDialog: false,
      selectedCheckId: null,
      scheduleDialog: false,
      savingSchedule: false,
      scheduleDays: [],
      items: [],
      editItems: [],
      form: {
        notes: "",
      },
      editForm: {
        notes: "",
      },
      loadError: "",
      filters: {
        from_date: this.defaultFromDate(),
        to_date: this.defaultToDate(),
        branch_id: null,
      },
      statusColumns: [
        { label: "Working", value: "working", color: "green" },
        { label: "Needs Repair", value: "needs_repair", color: "orange" },
        { label: "Broken", value: "broken", color: "red" },
        { label: "Missing", value: "missing", color: "deep-purple" },
        { label: "Present", value: "present", color: "blue" },
      ],
      weekdayOptions: [
        { text: "Sunday", value: 0 },
        { text: "Monday", value: 1 },
        { text: "Tuesday", value: 2 },
        { text: "Wednesday", value: 3 },
        { text: "Thursday", value: 4 },
        { text: "Friday", value: 5 },
        { text: "Saturday", value: 6 },
      ],
      historyHeaders: [
        { text: "Date", value: "check_date" },
        { text: "Branch", value: "branch", sortable: false },
        { text: "Checked By", value: "checked_by", sortable: false },
        { text: "Flagged Items", value: "flagged_count", sortable: false },
        { text: "Notes", value: "notes" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  methods: {
    ...mapActions({
      get_equipment_check_today: "facilities/get_equipment_check_today",
      submit_equipment_check: "facilities/submit_equipment_check",
      update_equipment_check: "facilities/update_equipment_check",
      get_equipment_checks: "facilities/get_equipment_checks",
      get_facility_check_settings: "facilities/get_facility_check_settings",
      update_facility_check_settings:
        "facilities/update_facility_check_settings",
    }),
    can(key) {
      return this.hasPermission(key);
    },
    groupByType(list) {
      const groups = [
        { title: "Facility Equipment", rows: [] },
        { title: "Tools", rows: [] },
      ];
      (list || []).forEach((i) => {
        const type = i.type || (i.equipment && i.equipment.type);
        groups[type === "tool" ? 1 : 0].rows.push(i);
      });
      return groups.filter((g) => g.rows.length);
    },
    openSchedule() {
      this.scheduleDays = [...this.scheduledDays];
      this.scheduleDialog = true;
    },
    async saveSchedule() {
      this.savingSchedule = true;
      try {
        await this.update_facility_check_settings({
          equipment_check_days: this.scheduleDays,
        });
        this.scheduleDialog = false;
        await this.$alert({
          title: "Saved",
          message: "Equipment check schedule updated.",
          color: "success",
        });
      } catch (e) {
        console.error(e);
        this.$alert({
          title: "Error",
          message:
            e.response &&
            (e.response.status === 422 || e.response.status === 403)
              ? e.response.data.message
              : "Failed to update the schedule.",
          color: "error",
        });
      }
      this.savingSchedule = false;
    },
    startEdit() {
      const check = this.todayCheck;
      this.editForm = {
        notes: check.notes || "",
      };
      this.editItems = (check.items || []).map((i) => ({
        facility_equipment_id: i.facility_equipment_id,
        name: i.equipment ? i.equipment.name : "—",
        designated_location: i.equipment ? i.equipment.designated_location : "",
        type: i.equipment && i.equipment.type ? i.equipment.type : "equipment",
        status: i.status || "working",
        remarks: i.remarks || "",
      }));
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
    },
    async saveEdit() {
      this.saving = true;
      try {
        await this.update_equipment_check({
          id: this.todayCheck.id,
          notes: this.editForm.notes,
          items: this.editItems.map((i) => ({
            facility_equipment_id: i.facility_equipment_id,
            status: i.status,
            remarks: i.remarks || null,
          })),
        });
        await this.$alert({
          title: "Saved",
          message: "Today's equipment check has been updated.",
          color: "success",
        });
        this.editing = false;
        await this.loadToday();
      } catch (e) {
        console.error(e);
        this.$alert({
          title: "Error",
          message:
            e.response &&
            (e.response.status === 422 || e.response.status === 403)
              ? e.response.data.message
              : "Failed to update the equipment check.",
          color: "error",
        });
      }
      this.saving = false;
    },
    defaultFromDate() {
      let d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().substring(0, 10);
    },
    defaultToDate() {
      return new Date().toISOString().substring(0, 10);
    },
    statusColor(status) {
      const colors = {
        working: "green",
        needs_repair: "orange",
        broken: "red",
        missing: "deep-purple",
        present: "blue",
      };
      return colors[status] || "grey";
    },
    statusLabel(status) {
      const labels = {
        working: "Working",
        needs_repair: "Needs Repair",
        broken: "Broken",
        missing: "Missing",
        present: "Present",
      };
      return labels[status] || status;
    },
    async loadToday() {
      this.loadingToday = true;
      this.loadError = "";
      try {
        await this.get_equipment_check_today();
        const equipment = this.equipment_check_today.equipment || [];
        this.items = equipment.map((e) => ({
          facility_equipment_id: e.id,
          name: e.name,
          designated_location: e.designated_location,
          type: e.type || "equipment",
          status: "",
          remarks: "",
        }));
      } catch (e) {
        console.error(e);
        this.items = [];
        const status = e.response && e.response.status;
        if (status === 403) {
          this.loadError =
            "You don't have permission to load the equipment checklist (missing 'dashboard.facility_equipment_checks').";
        } else if (status) {
          this.loadError =
            "Couldn't load the equipment checklist (server error " +
            status +
            "). Please try again or contact an admin.";
        } else {
          this.loadError =
            "Couldn't reach the server to load the equipment checklist.";
        }
      }
      this.loadingToday = false;
    },
    async submit() {
      const unchecked = this.items.filter((i) => !i.status);
      if (unchecked.length) {
        this.$alert({
          title: "Incomplete checklist",
          message:
            "Please set a status for every equipment item before submitting (" +
            unchecked.length +
            " remaining).",
          color: "warning",
        });
        return;
      }
      this.submitting = true;
      try {
        await this.submit_equipment_check({
          notes: this.form.notes,
          items: this.items.map((i) => ({
            facility_equipment_id: i.facility_equipment_id,
            status: i.status,
            remarks: i.remarks || null,
          })),
        });
        await this.$alert({
          title: "Submitted",
          message: "Today's equipment check has been submitted.",
          color: "success",
        });
        await this.loadToday();
      } catch (e) {
        console.error(e);
        this.$alert({
          title: "Error",
          message:
            e.response && e.response.status === 422
              ? e.response.data.message
              : "Failed to submit the equipment check.",
          color: "error",
        });
      }
      this.submitting = false;
    },
    async search() {
      this.loadingHistory = true;
      try {
        await this.get_equipment_checks(this.filters);
      } catch (e) {
        console.error(e);
      }
      this.loadingHistory = false;
    },
    viewCheck(item) {
      this.selectedCheckId = item.id;
      this.detailDialog = true;
    },
  },
  watch: {
    activeTab(val) {
      if (val === 1) this.search();
    },
    filters: {
      deep: true,
      handler() {
        if (this.activeTab === 1) this.search();
      },
    },
  },
  mounted() {
    this.loadToday();
    this.get_facility_check_settings().catch((e) => console.error(e));
    if (!this.branches || this.branches.length === 0) {
      this.$store.dispatch("branch/get_branches");
    }
  },
};
</script>

<style scoped>
.checklist-table th {
  white-space: nowrap;
}
</style>
